import * as Sentry from '@sentry/browser';
import { scrubSensitive, stripAccessTokenFragment } from '@susdle/shared';

export interface SentryClientOptions {
  dsn?: string | undefined;
  release?: string | undefined;
}

/**
 * Initialize Sentry for the browser. Mirrors the server-side pattern
 * (`packages/server/src/observability/sentry-error-reporter.ts`) — noop when
 * DSN absent so dev-mock, dev-real, and Vitest never emit Sentry traffic.
 *
 * Scrubbing rules match the server's: any value under a key in
 * `SENSITIVE_KEYS` (`secretWord`, `role`, `reconnectionToken`) is redacted
 * recursively. Additionally, `request.url` and any breadcrumb URL fields
 * have `#access_token=...` fragments redacted — Discord's OAuth `authorize`
 * leaves the token in `window.location.hash` momentarily, and any error
 * captured during that window would otherwise leak it.
 *
 * VERIFIED on 2026-05-30 against @sentry/browser@10.55.0
 * (signature confirmed in build/types/types/options.d.ts of @sentry/core@10.55.0:
 *  - `beforeSend?: (event: ErrorEvent, hint: EventHint) => ...` — line 588
 *  - `beforeBreadcrumb?: (breadcrumb: Breadcrumb, hint?: BreadcrumbHint) => ...` — line 625
 *  - `tracesSampleRate?: number` — line 244
 *  All names unchanged from prior major versions).
 */
export function initSentryClient(opts: SentryClientOptions): void {
  if (opts.dsn === undefined || opts.dsn === '') return;

  // Architecture §8: no perf tracing on client in Phase 1.
  // Tracing stays uninitialized by omitting `tracesSampleRate` entirely
  // (a value of 0 would still initialize the tracing infrastructure).
  Sentry.init({
    dsn: opts.dsn,
    release: opts.release,
    /* Discord Activity CSP blocks connect-src to *.ingest.sentry.io. Route
       envelopes through the same-origin /api/sentry CF Pages Function which
       forwards to the real ingest URL. See functions/api/sentry.ts. */
    tunnel: '/api/sentry',
    beforeSend(event) {
      const scrubbed = scrubSensitive(event) as typeof event;
      if (scrubbed.request?.url !== undefined) {
        scrubbed.request.url = stripAccessTokenFragment(scrubbed.request.url);
      }
      return scrubbed;
    },
    beforeBreadcrumb(crumb) {
      const scrubbed = scrubSensitive(crumb) as typeof crumb;
      if (scrubbed.data?.url !== undefined && typeof scrubbed.data.url === 'string') {
        scrubbed.data.url = stripAccessTokenFragment(scrubbed.data.url);
      }
      return scrubbed;
    },
  });
}
